import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { getPostBySlug, getPostComments, createComment } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Calendar, ArrowLeft, User, Send, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [commentForm, setCommentForm] = useState({
    author_name: '',
    author_email: '',
    content: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const postRes = await getPostBySlug(slug);
        setPost(postRes.data);
        
        const commentsRes = await getPostComments(postRes.data.id);
        setComments(commentsRes.data);
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await createComment({
        blog_post_id: post.id,
        ...commentForm
      });
      toast.success('Comment submitted! It will appear after admin approval.');
      setCommentForm({ author_name: '', author_email: '', content: '' });
    } catch (error) {
      toast.error('Failed to submit comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-blue border-t-transparent"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-brand-blue mb-4">Post Not Found</h1>
          <Link to="/blog">
            <Button>Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-neutral" data-testid="blog-post-page">
      {/* Header */}
      <section className="bg-brand-blue text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft size={20} className="mr-2" />
            Back to Blog
          </Link>
          {post.category_name && (
            <Badge className="bg-brand-teal text-white mb-4">
              {post.category_name}
            </Badge>
          )}
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-4 tracking-tight" data-testid="post-title">
            {post.title}
          </h1>
          <div className="flex items-center text-white/80 text-sm">
            <Calendar size={16} className="mr-2" />
            {formatDate(post.created_at)}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {post.featured_image && (
              <img 
                src={post.featured_image} 
                alt={post.title}
                className="w-full h-[400px] object-cover"
              />
            )}
            <CardContent className="p-8 md:p-12">
              <div 
                className="blog-content prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: post.content }}
                data-testid="post-content"
              />
              
              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-8 pt-8 border-t">
                  <h4 className="text-sm font-semibold text-gray-500 mb-3">Tags:</h4>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-brand-blue border-brand-blue/30">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comments Section */}
          <div className="mt-12">
            <h2 className="font-heading text-2xl font-bold text-brand-blue mb-6 flex items-center">
              <MessageSquare className="mr-3" size={24} />
              Comments ({comments.length})
            </h2>

            {/* Existing Comments */}
            {comments.length > 0 && (
              <div className="space-y-4 mb-8">
                {comments.map((comment) => (
                  <Card key={comment.id} className="bg-white p-6 rounded-xl" data-testid={`comment-${comment.id}`}>
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-brand-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <User size={20} className="text-brand-teal" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-brand-blue">{comment.author_name}</h4>
                          <span className="text-sm text-gray-400">{formatDate(comment.created_at)}</span>
                        </div>
                        <p className="text-gray-600">{comment.content}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Comment Form */}
            <Card className="bg-white p-8 rounded-2xl">
              <h3 className="font-heading text-xl font-bold text-brand-blue mb-6">Leave a Comment</h3>
              <form onSubmit={handleSubmitComment} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="author_name">Name *</Label>
                    <Input
                      id="author_name"
                      value={commentForm.author_name}
                      onChange={(e) => setCommentForm({...commentForm, author_name: e.target.value})}
                      required
                      data-testid="comment-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="author_email">Email *</Label>
                    <Input
                      id="author_email"
                      type="email"
                      value={commentForm.author_email}
                      onChange={(e) => setCommentForm({...commentForm, author_email: e.target.value})}
                      required
                      data-testid="comment-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="content">Comment *</Label>
                  <Textarea
                    id="content"
                    rows={4}
                    value={commentForm.content}
                    onChange={(e) => setCommentForm({...commentForm, content: e.target.value})}
                    required
                    data-testid="comment-content-input"
                  />
                </div>
                <p className="text-sm text-gray-500">
                  Your comment will be reviewed before being published.
                </p>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="bg-brand-orange hover:bg-brand-orange/90 text-white"
                  data-testid="submit-comment-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Comment'}
                  <Send size={16} className="ml-2" />
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
